export type BgRow = {
  background: string
  row: number // 1-based, matches the css grid-row the storefront renders
}

type Grid = string[][]

type Span = { start: number; end: number } // 0-based, inclusive

function getSpans (grid: Grid): Record<string, Span> {
  const spans: Record<string, Span> = {}
  grid.forEach((row, y) => {
    for (const id of row) {
      if (id === '.') continue
      if (!spans[id]) spans[id] = { start: y, end: y }
      else spans[id].end = y
    }
  })
  return spans
}

/**
 * Remaps row-bound background colors after the grid structure changed.
 *
 * Each colored row is anchored to its leftmost component that still exists in
 * the new grid. The color follows that component to its new row (keeping the
 * offset for components spanning multiple rows). Rows whose components were
 * all removed lose their color. If a color covered the full span of its
 * anchor component and that component grew, the color expands with it; if the
 * component shrank, the bottom color rows are dropped.
 */
export default function remapBackgroundRows (
  rows: BgRow[],
  prevGrid: Grid,
  nextGrid: Grid
): BgRow[] {
  const prevSpans = getSpans(prevGrid)
  const nextSpans = getSpans(nextGrid)

  type Mapped = BgRow & { anchor: string }
  const mapped: Mapped[] = []
  const byAnchor: Record<string, { offsets: number[]; backgrounds: Set<string> }> = {}

  for (const bgRow of rows) {
    const y = bgRow.row - 1
    const prevRow = prevGrid[y]
    if (!prevRow) continue // stale entry outside the grid

    const anchor = prevRow.find((id) => id !== '.' && nextSpans[id])
    if (!anchor) continue // no component of this row survived

    const offset = y - prevSpans[anchor].start
    if (!byAnchor[anchor]) byAnchor[anchor] = { offsets: [], backgrounds: new Set() }
    byAnchor[anchor].offsets.push(offset)
    byAnchor[anchor].backgrounds.add(bgRow.background)

    const nextSpan = nextSpans[anchor]
    const nextLen = nextSpan.end - nextSpan.start + 1
    if (offset >= nextLen) continue // anchor shrank → bottom rows are dropped

    mapped.push({
      row: nextSpan.start + offset + 1,
      background: bgRow.background,
      anchor
    })
  }

  // if a color covered the full previous span of its anchor, it should cover
  // the full new span as well (component grew → color grows with it)
  for (const anchor in byAnchor) {
    const info = byAnchor[anchor]
    if (info.backgrounds.size !== 1) continue
    const prevLen = prevSpans[anchor].end - prevSpans[anchor].start + 1
    const covered = new Set(info.offsets)
    if (covered.size !== prevLen) continue

    const background = Array.from(info.backgrounds)[0]
    const nextSpan = nextSpans[anchor]
    for (let y = nextSpan.start; y <= nextSpan.end; y++) {
      const exists = mapped.some((m) => m.anchor === anchor && m.row === y + 1)
      if (!exists) mapped.push({ row: y + 1, background, anchor })
    }
  }

  // two previous rows can collapse onto the same new row → first one wins
  const seen = new Set<number>()
  const result: BgRow[] = []
  for (const m of mapped.sort((a, b) => a.row - b.row)) {
    if (seen.has(m.row)) continue
    seen.add(m.row)
    result.push({ background: m.background, row: m.row })
  }
  return result
}
